import CampanaNotificaciones from "./CampanaNotificaciones";
import BotonInstructivo from "./BotonInstructivo";
import BotonMapaProceso from "./BotonMapaProceso";
import { notificarNCAbierta } from "./NotificadorALQOS";
import { useState, useEffect } from "react";
import { subirDatos, eliminarDato } from './syncSupabase';
const STORAGE_NC = 'alqos_ncs';
const STORAGE_FMEA = 'alqos_fmea';
const STORAGE_PC = 'alqos_plancontrol';
const STORAGE_LISTAS = 'alqos_listas_nc';
const STORAGE_FAMILIAS = 'alqos_familias';
const STORAGE_MODOS_FALLA = 'alqos_modos_falla';
const STORAGE_MAQUINAS = 'alqos_maquinas';
// Taxonomía cerrada para reincidencia (distinta del "motivo" de texto libre existente).
// Solo Administrador puede agregar valores nuevos — ver soloAdminAgrega en SelectorConOpciones.
const MODOS_FALLA_DEFAULT = ["Registro de color", "Gramaje fuera de spec", "Adhesión deficiente", "Corte fuera de medida", "Troquelado defectuoso", "Otro"];
const LOG = {
  info: (msg, data) => console.log(`[NC][INFO] ${msg}`, data || ''),
  error: (msg, err) => console.error(`[NC][ERROR] ${msg}`, err || ''),
};
const storage = {
  get: (key) => { try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) { LOG.error(`get ${key}`, e); return []; } },
  set: (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); return true; } catch (e) { LOG.error(`set ${key}`, e); return false; } },
};
const estadosNC = {
  abierta:     { label: "🔴 Abierta",     color: "#e63946" },
  en_analisis: { label: "🔵 En análisis", color: "#3b82f6" },
  en_proceso:  { label: "🟡 En proceso",  color: "#f59e0b" },
  cerrada:     { label: "✅ Cerrada",      color: "#22c55e" },
};
const severidades = {
  critica: { label: "🔴 Crítica", color: "#e63946" },
  mayor:   { label: "🟡 Mayor",   color: "#f59e0b" },
  menor:   { label: "🟢 Menor",   color: "#22c55e" },
};
const escalamientoColor = { bajo: "#22c55e", medio: "#f59e0b", alto: "#e63946" };
const escalamientoLabel = { bajo: "🟢 Bajo", medio: "🟡 Medio", alto: "🔴 Alto" };
const LISTAS_DEFAULT = {
  origen:      ["DEPTO PREPRENSA","Impresión Offset","Post Prensa","Control de Calidad","Expedición","Recepción MP","Cliente Externo","Auditoría interna","Auditoría externa","Autodetección"],
  estado:      ["En revisión","Pendiente cliente","Pendiente proveedor","En espera de materiales"],
  deteccion:   ["PRODUCCION","PRE PRENSA","CONTROL DE CALIDAD","EXPEDICION","CLIENTE","RECEPCION","AUDITORIA"],
  disposicion: ["Reprocesar","Reclasificar","Desechar","Liberado con desviación","Devolver a proveedor","Devolución a cliente"],
  motivo:      ["Chapa mal Copiada","Falla de registro","Gramaje fuera de spec","Error de color","Adhesión deficiente","Corte fuera de medida","Problema de barniz","Error de archivo digital","Humedad","Otro"],
  tipoNC:      ["INTERNA","EXTERNA","RECLAMO CLIENTE","HALLAZGO AUDITORIA","INCUMPLIMIENTO FSC","INCUMPLIMIENTO AMBIENTAL","PROVEEDOR","OPORTUNIDAD DE MEJORA"],
  unidad:      ["unidad","kg","pliego","resma","rollo","metro","caja"],
  metodoveri:  ["Inspección visual","Medición dimensional","Control estadístico","Revisión documental","Validación con cliente","Re-proceso y verificación"],
};
function generarNCI(lista) {
  const anio = new Date().getFullYear();
  // No usamos lista.length+1: si la lista está incompleta (memoria local vieja,
  // filtro, caché desincronizada de Supabase) eso repite un código ya usado —
  // mismo bug que se encontró y arregló en IngresoMateriales.js (IM-2026-001
  // duplicado). En vez de contar, buscamos el número más alto ya usado en los
  // códigos NCI existentes y seguimos desde ahí.
  const re = /^NCI-\d{4}-(\d+)$/;
  let maxNum = 0;
  lista.forEach(nc => {
    const m = re.exec(nc?.nci || "");
    if (m) maxNum = Math.max(maxNum, parseInt(m[1], 10));
  });
  const numero = String(maxNum + 1).padStart(3, "0");
  return `NCI-${anio}-${numero}`;
}
function fechaHoraActual() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}
function obtenerDatosFMEA(idFMEA) {
  try { const fmeas = storage.get(STORAGE_FMEA); return fmeas.find(f => f.idFMEA === idFMEA) || null; } catch { return null; }
}
function obtenerDatosPC(idPC) {
  try { const controles = storage.get(STORAGE_PC); return controles.find(c => c.idPC === idPC) || null; } catch { return null; }
}
function exportarCSV(datos, nombreArchivo) {
  if (!datos.length) { alert("No hay datos para exportar."); return; }
  const keys = ["nci","rip","fecha","trabajo","codigoProducto","cliente","op","proceso","sector","maquinaNombre","tipoNC","origen","deteccion","estado","severidad","escalamiento","descripcion","cantidad","unidad","cantidadProduccion","disposicion","motivo","estuRecup","estuScrap","precioUnitario","totalScrap","accionInmediata","causaRaiz","analisisDetallado","metodo5W","accionCorrectiva","accionPreventiva","responsable","fechaCompromiso","metodoverificacion","plazoVerificacion","verificacionEficacia","corroboracion","notaCredito","fechaDevolucion","esFSC","creadoPor","fechaCierre","cerradoPor"];
  const header = keys.join(",");
  const rows = datos.map(d => keys.map(k => `"${String(d[k] || "").replace(/"/g, '""')}"`).join(","));
  const csv = [header, ...rows].join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${nombreArchivo}_${new Date().toISOString().slice(0,10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
}
function exportarPDF(datos, titulo) {
  if (!datos.length) { alert("No hay datos para exportar."); return; }
  const campos = [["NCI","nci"],["RIP","rip"],["Fecha","fecha"],["Trabajo","trabajo"],["Código Producto","codigoProducto"],["Cliente","cliente"],["OP","op"],["Proceso","proceso"],["Máquina","maquinaNombre"],["Tipo NC","tipoNC"],["Origen","origen"],["Detección","deteccion"],["Severidad","severidad"],["Escalamiento","escalamiento"],["Descripción","descripcion"],["Cant. afectada","cantidad"],["Unidad","unidad"],["Cant. producción","cantidadProduccion"],["Disposición","disposicion"],["Motivo","motivo"],["Estudio recuperable","estuRecup"],["Estudio scrap","estuScrap"],["Precio x unidad","precioUnitario"],["Total scrap $","totalScrap"],["Acción inmediata","accionInmediata"],["Causa raíz","causaRaiz"],["5W?","metodo5W"],["Acción correctiva","accionCorrectiva"],["Acción preventiva","accionPreventiva"],["Responsable","responsable"],["Fecha compromiso","fechaCompromiso"],["Método verificación","metodoverificacion"],["Verificación eficacia","verificacionEficacia"],["Nota de crédito","notaCredito"],["Fecha devolución","fechaDevolucion"],["FSC","esFSC"],["Creado por","creadoPor"],["Fecha cierre","fechaCierre"],["Cerrado por","cerradoPor"]];
  const filas = datos.map(d => `
    <div style="page-break-inside:avoid;border:1px solid #ddd;border-radius:8px;padding:12px;margin-bottom:12px">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">
        <span style="font-size:15px;font-weight:700;color:#e63946">${d.nci || "—"}</span>
        <span style="font-size:12px;color:#808085">${d.estado || "—"} · ${d.severidad || "—"}</span>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:11px">
        ${campos.filter(([,k]) => d[k]).map(([l,k]) => `<tr><td style="padding:3px 6px;color:#808085;width:40%;border-bottom:.5px solid #eee">${l}</td><td style="padding:3px 6px;border-bottom:.5px solid #eee">${d[k]}</td></tr>`).join("")}
      </table>
    </div>`).join("");
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${titulo}</title>
    <style>body{font-family:Arial,sans-serif;padding:20px}h1{color:#09184D;font-size:16px}@media print{body{padding:10px}}</style></head>
    <body><h1>ALQOS™ — ${titulo}</h1>
    <p style="font-size:10px;color:#808085">Generado: ${new Date().toLocaleString('es-AR')} | Total: ${datos.length}</p>
    ${filas}</body></html>`;
  const ventana = window.open("", "_blank");
  ventana.document.write(html);
  ventana.document.close();
  setTimeout(() => ventana.print(), 500);
}
// ── SELECTOR CON OPCIÓN PERSONALIZADA ───────────────────────
function SelectorConOpciones({ campo, listas, onActualizarListas, valor, onChange, style, placeholder, soloAdminAgrega, esAdmin }) {
  const [mostrando, setMostrando] = useState(false);
  const [nueva, setNueva] = useState("");
  const opciones = listas[campo] || [];
  const puedeAgregar = !soloAdminAgrega || esAdmin;
  const agregar = () => {
    if (!nueva.trim()) return;
    const nuevas = { ...listas, [campo]: [...opciones, nueva.trim()] };
    onActualizarListas(nuevas);
    onChange(nueva.trim());
    setNueva("");
    setMostrando(false);
  };
  const eliminar = (op, e) => {
    e.stopPropagation();
    const nuevas = { ...listas, [campo]: opciones.filter(o => o !== op) };
    onActualizarListas(nuevas);
    if (valor === op) onChange("");
  };
  return (
    <div style={{ position: "relative" }}>
      <select value={valor || ""} onChange={e => { if (e.target.value === "__agregar__") { setMostrando(true); } else { onChange(e.target.value); }}} style={style}>
        <option value="">{placeholder || "— Seleccionar —"}</option>
        {opciones.map(op => <option key={op} value={op}>{op}</option>)}
        {puedeAgregar && <option value="__agregar__">+ Agregar nueva opción...</option>}
      </select>
      {mostrando && puedeAgregar && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setMostrando(false)}>
          <div style={{ background: "white", borderRadius: "14px", padding: "1.5rem", width: "340px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }} onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: "14px", fontWeight: "700", color: "#09184D", marginBottom: "12px" }}>Gestionar opciones — {campo}</h3>
            <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
              <input value={nueva} onChange={e => setNueva(e.target.value)} onKeyDown={e => e.key === "Enter" && agregar()} placeholder="Nueva opción..." style={{ flex: 1, padding: "8px 12px", borderRadius: "8px", border: "1px solid #ddd", fontSize: "13px" }}/>
              <button onClick={agregar} style={{ padding: "8px 14px", background: "#7B5BF2", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px" }}>+ Agregar</button>
            </div>
            <div style={{ maxHeight: "200px", overflowY: "auto" }}>
              {opciones.map(op => (
                <div key={op} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", borderRadius: "6px", marginBottom: "4px", background: "#f8f9fa", fontSize: "13px" }}>
                  <span>{op}</span>
                  <button onClick={e => eliminar(op, e)} style={{ background: "none", border: "none", color: "#e63946", cursor: "pointer", fontSize: "16px", lineHeight: 1 }}>×</button>
                </div>
              ))}
            </div>
            <button onClick={() => setMostrando(false)} style={{ marginTop: "12px", width: "100%", padding: "10px", background: "#09184D", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px" }}>Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
}
export default function NoConformidades({ onVolver, oscuro, usuario, onAnalisis }) {
  const [ncs, setNcs] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [listas, setListas] = useState(() => {
    try {
      const guardadas = JSON.parse(localStorage.getItem(STORAGE_LISTAS) || 'null');
      if (guardadas) {
        // Merge: agregar keys nuevas que no estén en las guardadas
        const merged = { ...LISTAS_DEFAULT };
        Object.keys(guardadas).forEach(k => { merged[k] = guardadas[k]; });
        return merged;
      }
      return LISTAS_DEFAULT;
    } catch { return LISTAS_DEFAULT; }
  });
  const [familias] = useState(() => { try { return JSON.parse(localStorage.getItem(STORAGE_FAMILIAS) || '[]'); } catch { return []; } });
  const [modosFalla, setModosFalla] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_MODOS_FALLA) || 'null') || MODOS_FALLA_DEFAULT; } catch { return MODOS_FALLA_DEFAULT; }
  });
  const actualizarModosFalla = (nuevos) => { setModosFalla(nuevos); localStorage.setItem(STORAGE_MODOS_FALLA, JSON.stringify(nuevos)); };
  const [vista, setVista] = useState("listado");
  const [form, setForm] = useState({ escalamiento: "bajo", estado: "abierta", severidad: "mayor" });
  const [ncDetalle, setNcDetalle] = useState(null);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [errores, setErrores] = useState({});
  const [filtros, setFiltros] = useState({ estado: "todos", origen: "todos", severidad: "todos", tipoNC: "todos" });
  const [busqueda, setBusqueda] = useState("");
  // OTs reales para el selector de "OP asociada" (antes era texto libre sin
  // validar contra alqos_ots -- Trazabilidad.js reconstruye la cadena NC→OT
  // comparando ese campo como texto, así que un typo rompía el vínculo en
  // silencio). Se sincroniza acá mismo, igual que ncs, para no depender de
  // que otro módulo la haya cargado antes en esta PC.
  const [otsDisponibles, setOtsDisponibles] = useState(() => {
    try { return JSON.parse(localStorage.getItem('alqos_ots') || '[]'); } catch { return []; }
  });
  // Catálogo de máquinas -- para poder vincular una NC cargada a mano a un
  // equipo puntual del catálogo real, igual que ya se puede en TPM, FMEA y
  // Capabilidad. Sin esto, la mitad de la trazabilidad por máquina se
  // pierde: solo las NC generadas automáticamente desde Control de Piso
  // traían maquinaId; las cargadas a mano en este módulo, y las generadas
  // desde TPM, no. Mismo patrón de solo lectura ya probado en los otros tres.
  const [maquinas, setMaquinas] = useState([]);
  useEffect(() => {
    async function cargarMaquinas() {
      try {
        const { supabase } = await import('./supabase');
        const { data } = await supabase.from('maquinas').select('data');
        if (data?.length) setMaquinas(data.map(r => r.data));
        else setMaquinas(storage.get(STORAGE_MAQUINAS));
      } catch (e) { setMaquinas(storage.get(STORAGE_MAQUINAS)); }
    }
    cargarMaquinas();
  }, []);
  useEffect(() => {
    async function cargar() {
      try {
        const { supabase } = await import('./supabase');
        const [{ data }, { data: dataOts }] = await Promise.all([
          supabase.from('ncs').select('data'),
          supabase.from('ots').select('data'),
        ]);
        if (data && data.length > 0) {
          const items = data.map(r => r.data);
          setNcs(items); storage.set(STORAGE_NC, items);
        } else { setNcs(storage.get(STORAGE_NC)); }
        if (dataOts && dataOts.length > 0) {
          const ots = dataOts.map(r => r.data);
          setOtsDisponibles(ots);
          localStorage.setItem('alqos_ots', JSON.stringify(ots));
        }
      } catch { setNcs(storage.get(STORAGE_NC)); }
      setCargando(false);
    }
    cargar();
  }, []);
  const actualizarListas = (nuevas) => {
    setListas(nuevas);
    localStorage.setItem(STORAGE_LISTAS, JSON.stringify(nuevas));
  };
  const bg       = oscuro ? "#09184D" : "#F0EEFF";
  const cardBg   = oscuro ? "#0d2160" : "white";
  const texto    = oscuro ? "#EDF2FA" : "#2F3E2E";
  const borde    = oscuro ? "#1e3a8a" : "#eee";
  const navBg    = oscuro ? "#060f30" : "#09184D";
  const subtexto = oscuro ? "#9aabd4" : "#808085";
  const esAdmin  = usuario?.rol === "Administrador";
  const inp = { width: "100%", padding: "8px 12px", borderRadius: "8px", border: `1px solid ${borde}`, fontSize: "13px", boxSizing: "border-box", marginTop: "4px", background: cardBg, color: texto, fontFamily: "sans-serif" };
  const lbl = { fontSize: "12px", color: oscuro ? "#9aabd4" : "#555", fontWeight: "600", display: "block", marginTop: "12px" };
  const card = { background: cardBg, borderRadius: "12px", padding: "1.5rem", border: `1px solid ${borde}`, marginBottom: "12px" };
  if (cargando) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: bg }}>
      <p style={{ color: oscuro ? "white" : "#09184D", fontSize: "16px" }}>Cargando NC...</p>
    </div>
  );
  const validar = () => {
    const e = {};
    if (!form.origen) e.origen = "Requerido";
    if (!form.proceso?.trim()) e.proceso = "Requerido";
    if (!form.descripcion?.trim()) e.descripcion = "Requerido";
    if (!form.tipoNC) e.tipoNC = "Requerido";
    if (!form.severidad) e.severidad = "Requerido";
    setErrores(e);
    return Object.keys(e).length === 0;
  };
  const validarCierre = (nc) => {
    if (!nc.accionCorrectiva?.trim()) { alert("No se puede cerrar sin acción correctiva definida."); return false; }
    if (!nc.verificacionEficacia?.trim()) { alert("No se puede cerrar sin verificación de eficacia."); return false; }
    return true;
  };
  const guardarNC = () => {
    if (!validar()) return;
    const ncsActuales = storage.get(STORAGE_NC);
    const nueva = {
      ...form,
      nci: generarNCI(ncsActuales),
      id: Date.now(),
      fecha: form.fecha || fechaHoraActual(),
      creadoPor: usuario?.nombre || "Sistema",
      rolCreador: usuario?.rol || "",
      escalamiento: form.escalamiento || "bajo",
      estado: "abierta",
      totalScrap: form.precioUnitario && form.estuScrap ? (parseFloat(form.precioUnitario) * parseFloat(form.estuScrap)).toFixed(2) : "",
      generadaAutomaticamente: false,
    };
    const nuevasNcs = [...ncsActuales, nueva];
    storage.set(STORAGE_NC, nuevasNcs);
    subirDatos(STORAGE_NC);
    setNcs(nuevasNcs);
    setForm({ escalamiento: "bajo", estado: "abierta", severidad: "mayor" });
    setErrores({});
    setVista("listado");
    notificarNCAbierta(nueva, usuario?.email || "admin@alqos.com", usuario);
  };
  const cambiarEstado = (nci, nuevoEstado) => {
    const nc = ncs.find(n => n.nci === nci);
    if (nuevoEstado === "cerrada" && !validarCierre(nc)) return;
    const actualizadas = ncs.map(n => n.nci === nci ? {
      ...n, estado: nuevoEstado,
      ...(nuevoEstado === "cerrada" ? { fechaCierre: fechaHoraActual(), cerradoPor: usuario?.nombre } : {})
    } : n);
    setNcs(actualizadas); storage.set(STORAGE_NC, actualizadas); subirDatos(STORAGE_NC);
    if (ncDetalle?.nci === nci) setNcDetalle(prev => ({ ...prev, estado: nuevoEstado }));
  };
  const actualizarCampo = (nci, campo, valor) => {
    const actualizadas = ncs.map(n => {
      if (n.nci !== nci) return n;
      const updated = { ...n, [campo]: valor };
      if (campo === 'precioUnitario' || campo === 'estuScrap') {
        const precio = parseFloat(campo === 'precioUnitario' ? valor : n.precioUnitario) || 0;
        const scrap = parseFloat(campo === 'estuScrap' ? valor : n.estuScrap) || 0;
        updated.totalScrap = precio && scrap ? (precio * scrap).toFixed(2) : "";
      }
      return updated;
    });
    setNcs(actualizadas); storage.set(STORAGE_NC, actualizadas); subirDatos(STORAGE_NC);
    if (ncDetalle?.nci === nci) setNcDetalle(prev => {
      const updated = { ...prev, [campo]: valor };
      if (campo === 'precioUnitario' || campo === 'estuScrap') {
        const precio = parseFloat(campo === 'precioUnitario' ? valor : prev.precioUnitario) || 0;
        const scrap = parseFloat(campo === 'estuScrap' ? valor : prev.estuScrap) || 0;
        updated.totalScrap = precio && scrap ? (precio * scrap).toFixed(2) : "";
      }
      return updated;
    });
  };
  const eliminarNC = async (nci) => {
    const nc = ncs.find(n => n.nci === nci);
    const nuevasNcs = ncs.filter(n => n.nci !== nci);
    setNcs(nuevasNcs); storage.set(STORAGE_NC, nuevasNcs);
    if (nc?.id) await eliminarDato(STORAGE_NC, nc.id);
    setConfirmEliminar(null); setNcDetalle(null); setVista("listado");
  };
  const ncsFiltradas = ncs.filter(n => {
    const matchEstado    = filtros.estado    === "todos" || n.estado    === filtros.estado;
    const matchOrigen    = filtros.origen    === "todos" || n.origen    === filtros.origen;
    const matchSeveridad = filtros.severidad === "todos" || n.severidad === filtros.severidad;
    const matchTipo      = filtros.tipoNC    === "todos" || n.tipoNC    === filtros.tipoNC;
    const matchBusqueda  = !busqueda || [n.nci,n.cliente,n.trabajo,n.descripcion,n.proceso,n.rip].some(v => v?.toLowerCase().includes(busqueda.toLowerCase()));
    return matchEstado && matchOrigen && matchSeveridad && matchTipo && matchBusqueda;
  });
  const resumen = {
    total:    ncs.length,
    abiertas: ncs.filter(n => n.estado === "abierta").length,
    criticas: ncs.filter(n => n.severidad === "critica").length,
    totalScrap: ncs.reduce((acc, n) => acc + (parseFloat(n.totalScrap) || 0), 0).toFixed(2),
  };
  const Selector = ({ campo, valor, onChange, placeholder }) => (
    <SelectorConOpciones campo={campo} listas={listas} onActualizarListas={actualizarListas}
      valor={valor} onChange={onChange} style={inp} placeholder={placeholder}/>
  );
  const Header = ({ titulo, onBack, extra }) => (
    <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, marginBottom: "1.5rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Volver</button>
        <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>{titulo}</span>
      </div>
      {extra}
    </div>
  );
  // ── CONFIRM ELIMINAR ──
  if (confirmEliminar) return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: cardBg, borderRadius: "16px", padding: "2rem", maxWidth: "360px", width: "90%", border: `1px solid ${borde}`, textAlign: "center" }}>
        <p style={{ fontSize: "32px" }}>⚠️</p>
        <p style={{ fontSize: "15px", fontWeight: "600", color: texto }}>¿Eliminar {confirmEliminar}?</p>
        <p style={{ fontSize: "13px", color: subtexto, marginBottom: "1.5rem" }}>Esta acción no se puede deshacer.</p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <button onClick={() => setConfirmEliminar(null)} style={{ padding: "10px 20px", background: "none", border: `1px solid ${borde}`, borderRadius: "10px", cursor: "pointer", color: texto }}>Cancelar</button>
          <button onClick={() => eliminarNC(confirmEliminar)} style={{ padding: "10px 20px", background: "#e63946", border: "none", borderRadius: "10px", cursor: "pointer", color: "white", fontWeight: "600" }}>Eliminar</button>
        </div>
      </div>
    </div>
  );
  // ── DETALLE ──
  if (vista === "detalle" && ncDetalle) {
    const datosFMEA = ncDetalle.idFMEARelacionado ? obtenerDatosFMEA(ncDetalle.idFMEARelacionado) : null;
    const datosPC   = ncDetalle.idPlanControl     ? obtenerDatosPC(ncDetalle.idPlanControl)         : null;
    return (
      <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
        <Header titulo={ncDetalle.nci}
          onBack={() => { setNcDetalle(null); setVista("listado"); }}
          extra={<div style={{ display: "flex", gap: "8px" }}>
            <button onClick={() => exportarPDF([ncDetalle], `NC_${ncDetalle.nci}`)} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 12px", borderRadius: "8px", cursor: "pointer", fontSize: "12px" }}>📄 PDF</button>
            {onAnalisis && <button onClick={() => onAnalisis(ncDetalle)} style={{ background: "rgba(123,91,242,.25)", border: "1px solid rgba(123,91,242,.5)", color: "#b09cf5", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }}>🔬 Analizar causa</button>}
            {esAdmin && <button onClick={() => setConfirmEliminar(ncDetalle.nci)} style={{ background: "rgba(230,57,70,.2)", border: "1px solid rgba(230,57,70,.4)", color: "#e63946", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "12px" }}>🗑 Eliminar</button>}
          </div>}
        />
        <div style={{ padding: "0 1.5rem 2rem", maxWidth: "700px", margin: "0 auto" }}>
          {/* Trazabilidad */}
          {(datosFMEA || datosPC) && (
            <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#f0f8ff", border: `1px solid ${oscuro ? "#1e3a8a" : "#bde0fe"}` }}>
              <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#3b82f6", marginBottom: "12px" }}>🔗 Cadena de trazabilidad</h3>
              {datosFMEA && <div style={{ padding: "8px 12px", background: cardBg, borderRadius: "8px", marginBottom: "6px", border: `1px solid ${oscuro?"#7B5BF2":"#d4c8ff"}` }}>
                <p style={{ fontSize: "11px", color: subtexto, margin: "0 0 2px", fontWeight: "600" }}>FMEA</p>
                <p style={{ fontSize: "12px", color: texto, margin: 0 }}>{datosFMEA.idFMEA} · {datosFMEA.modofalla} · NPR: {datosFMEA.npr}</p>
              </div>}
              {datosPC && <div style={{ padding: "8px 12px", background: cardBg, borderRadius: "8px", border: `1px solid ${oscuro?"#7B5BF2":"#d4c8ff"}` }}>
                <p style={{ fontSize: "11px", color: subtexto, margin: "0 0 2px", fontWeight: "600" }}>Plan de Control</p>
                <p style={{ fontSize: "12px", color: texto, margin: 0 }}>{datosPC.idPC} · {datosPC.caracteristica}</p>
              </div>}
            </div>
          )}
          {/* Bloque 1 — Identificación */}
          <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#F0EEFF", border: `1px solid ${oscuro ? "#7B5BF2" : "#d4c8ff"}` }}>
            <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#7B5BF2", marginBottom: "10px" }}>Bloque 1 — Identificación</h3>
            {[["NCI",ncDetalle.nci],["RIP",ncDetalle.rip],["Fecha/Hora",ncDetalle.fecha],["Trabajo",ncDetalle.trabajo],["Código de producto",ncDetalle.codigoProducto],["OP",ncDetalle.op],["Cliente",ncDetalle.cliente],["Proceso",ncDetalle.proceso],["Sector",ncDetalle.sector],["Máquina vinculada",ncDetalle.maquinaNombre],["Creado por",`${ncDetalle.creadoPor} — ${ncDetalle.rolCreador}`],["ID Plan de Control",ncDetalle.idPlanControl],["ID FMEA",ncDetalle.idFMEARelacionado],["Familia / Línea",familias.find(f=>f.id===ncDetalle.idFamiliaLinea)?.nombre],["FSC",ncDetalle.esFSC?"✅ Sí":"No"]].map(([k,v]) => v && (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", padding: "4px 0", borderBottom: `0.5px solid ${borde}` }}>
                <span style={{ color: subtexto }}>{k}</span>
                <span style={{ color: texto, fontWeight: "500", maxWidth: "65%", textAlign: "right" }}>{v}</span>
              </div>
            ))}
            {esAdmin && (
              <>
                <span style={lbl}>Máquina vinculada (opcional)</span>
                <select defaultValue={ncDetalle.maquinaId || ""} onChange={e => {
                  const id = e.target.value ? Number(e.target.value) : null;
                  const m = maquinas.find(x => x.id === id);
                  actualizarCampo(ncDetalle.nci, "maquinaId", id);
                  actualizarCampo(ncDetalle.nci, "maquinaNombre", m ? (m.nombre + (m.modelo ? ` — ${m.modelo}` : "")) : null);
                }} style={inp}>
                  <option value="">— Sin vincular —</option>
                  {maquinas.map(m => <option key={m.id} value={m.id}>{m.nombre}{m.modelo ? ` — ${m.modelo}` : ""}</option>)}
                </select>
              </>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", padding: "4px 0", marginTop: "8px" }}>
              <span style={{ color: subtexto }}>Escalamiento</span>
              <span style={{ fontWeight: "700", color: escalamientoColor[ncDetalle.escalamiento] }}>{escalamientoLabel[ncDetalle.escalamiento]}</span>
            </div>
          </div>
          {/* Bloque 2 — Descripción */}
          <div style={card}>
            <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#e63946", marginBottom: "10px" }}>Bloque 2 — Descripción del desvío</h3>
            {[["Tipo NC",ncDetalle.tipoNC],["Origen",ncDetalle.origen],["Detección",ncDetalle.deteccion],["Severidad",ncDetalle.severidad ? severidades[ncDetalle.severidad]?.label : null],["Descripción",ncDetalle.descripcion],["Motivo",ncDetalle.motivo],["Modo de falla",ncDetalle.modoFalla],["Cant. afectada",ncDetalle.cantidad ? `${ncDetalle.cantidad} ${ncDetalle.unidad||""}` : null],["Cant. producción",ncDetalle.cantidadProduccion],["Disposición",ncDetalle.disposicion],["Estado adicional",ncDetalle.estadoAdicional],["Nota",ncDetalle.nota]].map(([k,v]) => v && (
              <div key={k} style={{ padding: "5px 0", borderBottom: `0.5px solid ${borde}` }}>
                <p style={{ fontSize: "11px", color: subtexto, margin: "0 0 1px", fontWeight: "600" }}>{k}</p>
                <p style={{ fontSize: "12px", color: texto, margin: 0 }}>{v}</p>
              </div>
            ))}
          </div>
          {/* Bloque 3 — Valorización */}
          <div style={{ ...card, background: oscuro ? "#0a1f0a" : "#f0fff4", border: `1px solid ${oscuro?"#22c55e44":"#bbf7d0"}` }}>
            <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#22c55e", marginBottom: "10px" }}>Bloque 3 — Valorización del costo</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px", marginBottom: "10px" }}>
              {[["Estudio recuperable",ncDetalle.estuRecup,"#22c55e"],["Estudio scrap",ncDetalle.estuScrap,"#e63946"],["Total scrap $",ncDetalle.totalScrap ? `$${ncDetalle.totalScrap}` : null,"#f59e0b"]].map(([k,v,c]) => (
                <div key={k} style={{ background: oscuro?"#091840":cardBg, borderRadius: "10px", padding: "10px", textAlign: "center", border: `1px solid ${borde}` }}>
                  <p style={{ fontSize: "10px", color: subtexto, margin: "0 0 4px" }}>{k}</p>
                  <p style={{ fontSize: "16px", fontWeight: "700", color: c, margin: 0 }}>{v || "—"}</p>
                </div>
              ))}
            </div>
            {[["Precio x unidad",ncDetalle.precioUnitario ? `$${ncDetalle.precioUnitario}` : null],["Nota de crédito",ncDetalle.notaCredito],["Fecha devolución",ncDetalle.fechaDevolucion]].map(([k,v]) => v && (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", padding: "4px 0", borderBottom: `0.5px solid ${borde}` }}>
                <span style={{ color: subtexto }}>{k}</span>
                <span style={{ color: texto }}>{v}</span>
              </div>
            ))}
            {esAdmin && (
              <div style={{ marginTop: "12px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
                {[["estuRecup","Estudio recuperable"],["estuScrap","Estudio scrap"],["precioUnitario","Precio x unidad"]].map(([campo,label]) => (
                  <div key={campo}>
                    <span style={{ ...lbl, marginTop: "4px" }}>{label}</span>
                    <input type="number" defaultValue={ncDetalle[campo]||""} onBlur={e => actualizarCampo(ncDetalle.nci, campo, e.target.value)} style={inp}/>
                  </div>
                ))}
                <div>
                  <span style={{ ...lbl, marginTop: "4px" }}>Nota de crédito</span>
                  <input defaultValue={ncDetalle.notaCredito||""} onBlur={e => actualizarCampo(ncDetalle.nci, "notaCredito", e.target.value)} style={inp}/>
                </div>
                <div>
                  <span style={{ ...lbl, marginTop: "4px" }}>Fecha devolución</span>
                  <input type="date" defaultValue={ncDetalle.fechaDevolucion||""} onBlur={e => actualizarCampo(ncDetalle.nci, "fechaDevolucion", e.target.value)} style={inp}/>
                </div>
              </div>
            )}
          </div>
          {/* Bloque 4 — Análisis */}
          <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#fff8f0", border: `1px solid ${oscuro?"#f59e0b44":"#fde68a"}` }}>
            <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#f59e0b", marginBottom: "10px" }}>Bloque 4 — Análisis de causa</h3>
            {[["Acción inmediata",ncDetalle.accionInmediata],["Corroboración",ncDetalle.corroboracion],["Causa raíz",ncDetalle.causaRaiz],["Análisis detallado",ncDetalle.analisisDetallado],["5W aplicado",ncDetalle.metodo5W]].map(([k,v]) => v && (
              <div key={k} style={{ padding: "5px 0", borderBottom: `0.5px solid ${borde}` }}>
                <p style={{ fontSize: "11px", color: subtexto, margin: "0 0 1px", fontWeight: "600" }}>{k}</p>
                <p style={{ fontSize: "12px", color: texto, margin: 0 }}>{v}</p>
              </div>
            ))}
            {esAdmin && (
              <div style={{ marginTop: "12px" }}>
                <span style={lbl}>Acción inmediata</span>
                <textarea rows={2} defaultValue={ncDetalle.accionInmediata||""} onBlur={e => actualizarCampo(ncDetalle.nci,"accionInmediata",e.target.value)} style={{ ...inp, resize:"none" }}/>
                <span style={lbl}>Corroboración</span>
                <input defaultValue={ncDetalle.corroboracion||""} onBlur={e => actualizarCampo(ncDetalle.nci,"corroboracion",e.target.value)} style={inp}/>
                <span style={lbl}>Causa raíz</span>
                <textarea rows={2} defaultValue={ncDetalle.causaRaiz||""} onBlur={e => actualizarCampo(ncDetalle.nci,"causaRaiz",e.target.value)} style={{ ...inp, resize:"none" }}/>
                <span style={lbl}>Análisis detallado (5 Porqués / Ishikawa)</span>
                <textarea rows={3} defaultValue={ncDetalle.analisisDetallado||""} onBlur={e => actualizarCampo(ncDetalle.nci,"analisisDetallado",e.target.value)} style={{ ...inp, resize:"none" }}/>
                <span style={lbl}>¿Se aplicó metodología 5W?</span>
                <select defaultValue={ncDetalle.metodo5W||""} onChange={e => actualizarCampo(ncDetalle.nci,"metodo5W",e.target.value)} style={inp}>
                  <option value="">— Seleccionar —</option>
                  <option value="Sí — 5 Porqués">Sí — 5 Porqués</option>
                  <option value="Sí — Ishikawa">Sí — Ishikawa</option>
                  <option value="No aplica">No aplica</option>
                </select>
              </div>
            )}
          </div>
          {/* Bloque 5 — Acción correctiva y cierre */}
          <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#f0fff4", border: `1px solid ${oscuro?"#22c55e44":"#bbf7d0"}` }}>
            <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#22c55e", marginBottom: "10px" }}>Bloque 5 — Acción correctiva y cierre</h3>
            {[["Acción correctiva",ncDetalle.accionCorrectiva],["Acción preventiva",ncDetalle.accionPreventiva],["Responsable",ncDetalle.responsable],["Solicitado por",ncDetalle.solicitadoPor],["Fecha compromiso",ncDetalle.fechaCompromiso],["Método de verificación",ncDetalle.metodoverificacion],["Plazo de verificación",ncDetalle.plazoVerificacion],["Verificación de eficacia",ncDetalle.verificacionEficacia],["Fecha cierre",ncDetalle.fechaCierre?.slice(0,10)],["Cerrado por",ncDetalle.cerradoPor]].map(([k,v]) => v && (
              <div key={k} style={{ padding: "5px 0", borderBottom: `0.5px solid ${borde}` }}>
                <p style={{ fontSize: "11px", color: subtexto, margin: "0 0 1px", fontWeight: "600" }}>{k}</p>
                <p style={{ fontSize: "12px", color: texto, margin: 0 }}>{v}</p>
              </div>
            ))}
            {esAdmin && ncDetalle.estado !== "cerrada" && (
              <div style={{ marginTop: "12px" }}>
                <span style={lbl}>Acción correctiva *</span>
                <textarea rows={2} defaultValue={ncDetalle.accionCorrectiva||""} onBlur={e => actualizarCampo(ncDetalle.nci,"accionCorrectiva",e.target.value)} style={{ ...inp, resize:"none" }}/>
                <span style={lbl}>Acción preventiva</span>
                <textarea rows={2} defaultValue={ncDetalle.accionPreventiva||""} onBlur={e => actualizarCampo(ncDetalle.nci,"accionPreventiva",e.target.value)} style={{ ...inp, resize:"none" }}/>
                <span style={lbl}>Responsable</span>
                <input defaultValue={ncDetalle.responsable||""} onBlur={e => actualizarCampo(ncDetalle.nci,"responsable",e.target.value)} style={inp}/>
                <span style={lbl}>Solicitado por</span>
                <input defaultValue={ncDetalle.solicitadoPor||""} onBlur={e => actualizarCampo(ncDetalle.nci,"solicitadoPor",e.target.value)} style={inp}/>
                <span style={lbl}>Fecha compromiso</span>
                <input type="date" defaultValue={ncDetalle.fechaCompromiso||""} onBlur={e => actualizarCampo(ncDetalle.nci,"fechaCompromiso",e.target.value)} style={inp}/>
                <span style={lbl}>Método de verificación</span>
                <SelectorConOpciones campo="metodoveri" listas={listas} onActualizarListas={actualizarListas} valor={ncDetalle.metodoverificacion||""} onChange={v => actualizarCampo(ncDetalle.nci,"metodoverificacion",v)} style={inp}/>
                <span style={lbl}>Plazo de verificación</span>
                <input type="date" defaultValue={ncDetalle.plazoVerificacion||""} onBlur={e => actualizarCampo(ncDetalle.nci,"plazoVerificacion",e.target.value)} style={inp}/>
                <span style={lbl}>Verificación de eficacia *</span>
                <textarea rows={2} defaultValue={ncDetalle.verificacionEficacia||""} onBlur={e => actualizarCampo(ncDetalle.nci,"verificacionEficacia",e.target.value)} style={{ ...inp, resize:"none" }}/>
              </div>
            )}
          </div>
          {/* FSC */}
          {ncDetalle.esFSC && (
            <div style={{ ...card, background: oscuro?"#0a1f5c":"#f0f4ff", border: "1px solid #3b82f680" }}>
              <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#3b82f6", marginBottom: "8px" }}>🌿 Tratamiento FSC — Plazos normativos</h3>
              <p style={{ fontSize: "12px", color: subtexto, margin: "0 0 4px" }}>⏱ Acuse de recibo al querellante: <strong style={{ color: texto }}>2 semanas desde la queja</strong></p>
              <p style={{ fontSize: "12px", color: subtexto, margin: "0 0 4px" }}>⏱ Acciones propuestas: <strong style={{ color: texto }}>3 meses desde la queja</strong></p>
              <p style={{ fontSize: "12px", color: subtexto, margin: 0 }}>⏱ Notificación al cliente y entidad certificadora al cierre</p>
            </div>
          )}
          {/* Estado */}
          {esAdmin && (
            <div style={card}>
              <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#7B5BF2", marginBottom: "10px" }}>Estado de la NC</h3>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {Object.entries(estadosNC).map(([val, { label, color }]) => (
                  <button key={val} onClick={() => cambiarEstado(ncDetalle.nci, val)}
                    style={{ padding: "7px 12px", borderRadius: "10px", fontSize: "12px", cursor: "pointer", fontWeight: "600", border: ncDetalle.estado === val ? `2px solid ${color}` : `1px solid ${borde}`, background: ncDetalle.estado === val ? `${color}22` : cardBg, color: ncDetalle.estado === val ? color : subtexto }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  // ── NUEVA NC ──
  if (vista === "nueva") return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Header titulo="Nueva No Conformidad" onBack={() => { setVista("listado"); setErrores({}); }}/>
      <div style={{ padding: "0 1.5rem 2rem", maxWidth: "700px", margin: "0 auto" }}>
        <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#F0EEFF", border: `1px solid ${oscuro?"#7B5BF2":"#d4c8ff"}` }}>
          <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#7B5BF2", marginBottom: "8px" }}>Bloque 1 — Identificación *</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            <div>
              <span style={lbl}>Fecha y hora *</span>
              <input type="datetime-local" defaultValue={fechaHoraActual()} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} style={inp}/>
            </div>
            <div>
              <span style={lbl}>RIP</span>
              <input onChange={e => setForm(f => ({ ...f, rip: e.target.value }))} style={inp} placeholder="N° registro interno"/>
            </div>
            <div>
              <span style={lbl}>OP asociada</span>
              {/* Antes era texto libre sin validar contra las OTs reales --
                  cualquier variación de formato ("OP-123" vs "OP123") rompía
                  en silencio el vínculo que usa Trazabilidad.js. Ahora se
                  elige de la lista real (alqos_ots); se sigue guardando solo
                  el código en form.op, igual que antes, para no romper
                  compatibilidad con NCs ya cargadas ni con Trazabilidad.js. */}
              <select value={form.op || ""} onChange={e => setForm(f => ({ ...f, op: e.target.value }))} style={inp}>
                <option value="">— Sin vincular / no aplica —</option>
                {otsDisponibles.map(o => <option key={o.op} value={o.op}>{o.op} · {o.cliente} · {o.trabajo?.slice(0,30)}</option>)}
              </select>
              {otsDisponibles.length === 0 && (
                <p style={{ fontSize: "10px", color: subtexto, margin: "4px 0 0" }}>No hay OTs cargadas todavía.</p>
              )}
            </div>
            <div>
              <span style={lbl}>Código de producto</span>
              <input onChange={e => setForm(f => ({ ...f, codigoProducto: e.target.value }))} style={inp}/>
            </div>
          </div>
          <span style={lbl}>Trabajo / Descripción del producto</span>
          <input onChange={e => setForm(f => ({ ...f, trabajo: e.target.value }))} style={inp} placeholder="Ej: Sobre Grande, Estuche Farmacéutico"/>
          <span style={lbl}>Cliente</span>
          <input onChange={e => setForm(f => ({ ...f, cliente: e.target.value }))} style={inp}/>
          <span style={lbl}>Proceso *{errores.proceso && <span style={{ color: "#e63946" }}> — {errores.proceso}</span>}</span>
          <input onChange={e => setForm(f => ({ ...f, proceso: e.target.value }))} style={{ ...inp, borderColor: errores.proceso?"#e63946":borde }} placeholder="Ej: Impresión Offset, Pre Prensa"/>
          <span style={lbl}>Sector</span>
          <input onChange={e => setForm(f => ({ ...f, sector: e.target.value }))} style={inp}/>
          {maquinas.length > 0 && (
            <>
              <span style={lbl}>Vincular a máquina cargada (opcional — habilita comparar NC entre equipos del mismo proceso)</span>
              <select value={form.maquinaId || ""} onChange={e => {
                const id = e.target.value ? Number(e.target.value) : null;
                const m = maquinas.find(x => x.id === id);
                setForm(f => ({ ...f, maquinaId: id, maquinaNombre: m ? (m.nombre + (m.modelo ? ` — ${m.modelo}` : "")) : null }));
              }} style={inp}>
                <option value="">— Sin vincular —</option>
                {maquinas.map(m => <option key={m.id} value={m.id}>{m.nombre}{m.modelo ? ` — ${m.modelo}` : ""}</option>)}
              </select>
            </>
          )}
          <span style={lbl}>Escalamiento</span>
          <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
            {[["bajo","🟢 Bajo"],["medio","🟡 Medio"],["alto","🔴 Alto"]].map(([val,lab]) => (
              <button key={val} onClick={() => setForm(f => ({ ...f, escalamiento: val }))}
                style={{ flex:1, padding:"8px", borderRadius:"10px", fontSize:"12px", cursor:"pointer", fontWeight:"600", border: form.escalamiento===val ? `2px solid ${escalamientoColor[val]}` : `1px solid ${borde}`, background: form.escalamiento===val ? `${escalamientoColor[val]}22` : cardBg, color: form.escalamiento===val ? escalamientoColor[val] : texto }}>
                {lab}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "12px" }}>
            <input type="checkbox" id="esFSC" onChange={e => setForm(f => ({ ...f, esFSC: e.target.checked }))} style={{ width: "16px", height: "16px" }}/>
            <label htmlFor="esFSC" style={{ fontSize: "13px", color: texto, cursor: "pointer" }}>🌿 Involucra cadena de custodia FSC</label>
          </div>
          <span style={lbl}>ID Plan de Control (si aplica)</span>
          <input onChange={e => setForm(f => ({ ...f, idPlanControl: e.target.value }))} style={inp} placeholder="Ej: PC-IMPRES-001"/>
          <span style={lbl}>ID FMEA relacionado (si aplica)</span>
          <input onChange={e => setForm(f => ({ ...f, idFMEARelacionado: e.target.value }))} style={inp} placeholder="Ej: FMEA-IMPRES-001"/>
          <span style={lbl}>Familia / Línea de SKU (para motor de fases de calidad)</span>
          <select value={form.idFamiliaLinea || ""} onChange={e => setForm(f => ({ ...f, idFamiliaLinea: e.target.value ? Number(e.target.value) : null }))} style={inp}>
            <option value="">— Sin vincular —</option>
            {familias.map(fam => <option key={fam.id} value={fam.id}>{fam.codigo} — {fam.nombre}</option>)}
          </select>
        </div>
        <div style={card}>
          <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#e63946", marginBottom: "8px" }}>Bloque 2 — Descripción del desvío *</h3>
          <span style={lbl}>Tipo de NC *{errores.tipoNC && <span style={{ color: "#e63946" }}> — {errores.tipoNC}</span>}</span>
          <Selector campo="tipoNC" valor={form.tipoNC} onChange={v => setForm(f => ({ ...f, tipoNC: v }))}/>
          <span style={lbl}>Origen *{errores.origen && <span style={{ color: "#e63946" }}> — {errores.origen}</span>}</span>
          <Selector campo="origen" valor={form.origen} onChange={v => setForm(f => ({ ...f, origen: v }))}/>
          <span style={lbl}>Detección</span>
          <Selector campo="deteccion" valor={form.deteccion} onChange={v => setForm(f => ({ ...f, deteccion: v }))}/>
          <span style={lbl}>Severidad *</span>
          <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
            {Object.entries(severidades).map(([val, { label, color }]) => (
              <button key={val} onClick={() => setForm(f => ({ ...f, severidad: val }))}
                style={{ flex:1, padding:"8px", borderRadius:"10px", fontSize:"12px", cursor:"pointer", fontWeight:"600", border: form.severidad===val ? `2px solid ${color}` : `1px solid ${borde}`, background: form.severidad===val ? `${color}22` : cardBg, color: form.severidad===val ? color : texto }}>
                {label}
              </button>
            ))}
          </div>
          <span style={lbl}>Motivo</span>
          <Selector campo="motivo" valor={form.motivo} onChange={v => setForm(f => ({ ...f, motivo: v }))}/>
          <span style={lbl}>Modo de falla (taxonomía cerrada — para cálculo de reincidencia)</span>
          <SelectorConOpciones campo="modoFalla" listas={{ modoFalla: modosFalla }} onActualizarListas={l => actualizarModosFalla(l.modoFalla)}
            valor={form.modoFalla} onChange={v => setForm(f => ({ ...f, modoFalla: v }))} style={inp} soloAdminAgrega esAdmin={esAdmin}/>
          <span style={lbl}>Descripción del desvío *{errores.descripcion && <span style={{ color: "#e63946" }}> — {errores.descripcion}</span>}</span>
          <textarea rows={3} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} style={{ ...inp, resize:"none", borderColor: errores.descripcion?"#e63946":borde }} placeholder="Describí claramente el desvío detectado..."/>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
            <div>
              <span style={lbl}>Cant. afectada</span>
              <input type="number" onChange={e => setForm(f => ({ ...f, cantidad: e.target.value }))} style={inp}/>
            </div>
            <div>
              <span style={lbl}>Unidad</span>
              <Selector campo="unidad" valor={form.unidad} onChange={v => setForm(f => ({ ...f, unidad: v }))}/>
            </div>
            <div>
              <span style={lbl}>Cant. producción</span>
              <input type="number" onChange={e => setForm(f => ({ ...f, cantidadProduccion: e.target.value }))} style={inp}/>
            </div>
          </div>
          <span style={lbl}>Disposición</span>
          <Selector campo="disposicion" valor={form.disposicion} onChange={v => setForm(f => ({ ...f, disposicion: v }))}/>
          <span style={lbl}>Estado adicional</span>
          <Selector campo="estado" valor={form.estadoAdicional} onChange={v => setForm(f => ({ ...f, estadoAdicional: v }))}/>
          <span style={lbl}>Nota</span>
          <textarea rows={2} onChange={e => setForm(f => ({ ...f, nota: e.target.value }))} style={{ ...inp, resize:"none" }}/>
        </div>
        <div style={{ ...card, background: oscuro?"#0a1f0a":"#f0fff4", border: `1px solid ${oscuro?"#22c55e44":"#bbf7d0"}` }}>
          <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#22c55e", marginBottom: "8px" }}>Bloque 3 — Valorización del costo</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" }}>
            <div>
              <span style={lbl}>Estudio recuperable</span>
              <input type="number" onChange={e => setForm(f => ({ ...f, estuRecup: e.target.value }))} style={inp}/>
            </div>
            <div>
              <span style={lbl}>Estudio scrap</span>
              <input type="number" onChange={e => setForm(f => ({ ...f, estuScrap: e.target.value }))} style={inp}/>
            </div>
            <div>
              <span style={lbl}>Precio x unidad $</span>
              <input type="number" onChange={e => setForm(f => ({ ...f, precioUnitario: e.target.value }))} style={inp}/>
            </div>
          </div>
          <span style={lbl}>Nota de crédito asociada</span>
          <input onChange={e => setForm(f => ({ ...f, notaCredito: e.target.value }))} style={inp}/>
          <span style={lbl}>Fecha devolución a cliente</span>
          <input type="date" onChange={e => setForm(f => ({ ...f, fechaDevolucion: e.target.value }))} style={inp}/>
        </div>
        <div style={{ ...card, background: oscuro?"#0a1f5c":"#fff8f0", border: `1px solid ${oscuro?"#f59e0b44":"#fde68a"}` }}>
          <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#f59e0b", marginBottom: "8px" }}>Bloque 4 — Análisis de causa</h3>
          <span style={lbl}>Acción inmediata</span>
          <textarea rows={2} onChange={e => setForm(f => ({ ...f, accionInmediata: e.target.value }))} style={{ ...inp, resize:"none" }}/>
          <span style={lbl}>Causa raíz</span>
          <textarea rows={2} onChange={e => setForm(f => ({ ...f, causaRaiz: e.target.value }))} style={{ ...inp, resize:"none" }}/>
          <span style={lbl}>Análisis detallado (5 Porqués / Ishikawa)</span>
          <textarea rows={3} onChange={e => setForm(f => ({ ...f, analisisDetallado: e.target.value }))} style={{ ...inp, resize:"none" }}/>
          <span style={lbl}>¿Se aplicó 5W?</span>
          <select onChange={e => setForm(f => ({ ...f, metodo5W: e.target.value }))} style={inp}>
            <option value="">— Seleccionar —</option>
            <option value="Sí — 5 Porqués">Sí — 5 Porqués</option>
            <option value="Sí — Ishikawa">Sí — Ishikawa</option>
            <option value="No aplica">No aplica</option>
          </select>
        </div>
        <div style={{ ...card, background: oscuro?"#0a1f5c":"#f0fff4", border: `1px solid ${oscuro?"#22c55e44":"#bbf7d0"}` }}>
          <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#22c55e", marginBottom: "8px" }}>Bloque 5 — Acción correctiva y cierre</h3>
          <span style={lbl}>Acción correctiva</span>
          <textarea rows={2} onChange={e => setForm(f => ({ ...f, accionCorrectiva: e.target.value }))} style={{ ...inp, resize:"none" }}/>
          <span style={lbl}>Acción preventiva</span>
          <textarea rows={2} onChange={e => setForm(f => ({ ...f, accionPreventiva: e.target.value }))} style={{ ...inp, resize:"none" }}/>
          <span style={lbl}>Responsable</span>
          <input onChange={e => setForm(f => ({ ...f, responsable: e.target.value }))} style={inp}/>
          <span style={lbl}>Solicitado por</span>
          <input onChange={e => setForm(f => ({ ...f, solicitadoPor: e.target.value }))} style={inp}/>
          <span style={lbl}>Fecha compromiso</span>
          <input type="date" onChange={e => setForm(f => ({ ...f, fechaCompromiso: e.target.value }))} style={inp}/>
          <span style={lbl}>Método de verificación</span>
          <Selector campo="metodoveri" valor={form.metodoverificacion} onChange={v => setForm(f => ({ ...f, metodoverificacion: v }))}/>
          <span style={lbl}>Plazo de verificación</span>
          <input type="date" onChange={e => setForm(f => ({ ...f, plazoVerificacion: e.target.value }))} style={inp}/>
          <span style={lbl}>Verificación de eficacia</span>
          <textarea rows={2} onChange={e => setForm(f => ({ ...f, verificacionEficacia: e.target.value }))} style={{ ...inp, resize:"none" }}/>
        </div>
        <button onClick={guardarNC} style={{ width:"100%", padding:"14px", background:"#7B5BF2", color:"white", border:"none", borderRadius:"10px", fontSize:"15px", fontWeight:"600", cursor:"pointer", marginTop:"1rem" }}>
          Guardar No Conformidad
        </button>
      </div>
    </div>
  );
  // ── LISTADO ──
  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button onClick={onVolver} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Menú</button>
          <BotonInstructivo moduloKey="ncs" oscuro={oscuro} />
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="ncs" oscuro={oscuro} />}
          <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>No Conformidades</span>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={() => exportarCSV(ncsFiltradas, "NoConformidades")} style={{ padding: "6px 10px", background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", color: "white" }}>📊 Excel</button>
          <button onClick={() => exportarPDF(ncsFiltradas, "No Conformidades")} style={{ padding: "6px 10px", background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", color: "white" }}>📄 PDF</button>
          <CampanaNotificaciones oscuro={oscuro} modulo="NC" />
          <button onClick={() => setVista("nueva")} style={{ padding: "6px 14px", background: "#e63946", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>+ Nueva NC</button>
        </div>
      </div>
      <div style={{ padding: "1rem 1.5rem" }}>
        {/* KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px,1fr))", gap: "10px", marginBottom: "1.2rem" }}>
          {[["Total",resumen.total,"#7B5BF2"],["Abiertas",resumen.abiertas,"#e63946"],["Críticas",resumen.criticas,"#f97316"],["Costo scrap",`$${resumen.totalScrap}`,"#f59e0b"]].map(([lab,val,col]) => (
            <div key={lab} style={{ background: cardBg, borderRadius: "12px", padding: "1rem", border: `1px solid ${lab==="Abiertas"&&resumen.abiertas>0?"#e63946":borde}`, textAlign: "center" }}>
              <p style={{ fontSize: "20px", fontWeight: "700", color: col, margin: 0 }}>{val}</p>
              <p style={{ fontSize: "11px", color: subtexto, margin: "4px 0 0" }}>{lab}</p>
            </div>
          ))}
        </div>
        {/* Búsqueda y filtros */}
        <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
          placeholder="🔍 Buscar por NCI, RIP, cliente, trabajo, descripción..."
          style={{ ...inp, marginBottom: "10px", marginTop: 0, background: cardBg }}/>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "1rem" }}>
          {[["estado","estado"],["origen","origen"],["severidad","severidad"],["tipoNC","tipoNC"]].map(([filtro, listaKey]) => (
            <select key={filtro} value={filtros[filtro]} onChange={e => setFiltros(f => ({ ...f, [filtro]: e.target.value }))}
              style={{ ...inp, marginTop: 0, flex: 1, minWidth: "120px", background: cardBg }}>
              <option value="todos">Todos — {filtro}</option>
              {(listaKey === "estado" ? Object.keys(estadosNC) : listaKey === "severidad" ? Object.keys(severidades) : listas[listaKey] || []).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ))}
        </div>
        {/* Lista */}
        {ncsFiltradas.length === 0
          ? <div style={{ textAlign: "center", padding: "4rem" }}>
              <p style={{ fontSize: "40px" }}>⚠️</p>
              <p style={{ fontSize: "15px", color: texto }}>No hay no conformidades registradas</p>
              <p style={{ fontSize: "13px", color: subtexto }}>Hacé clic en "+ Nueva NC" para comenzar</p>
            </div>
          : ncsFiltradas.map(nc => (
              <div key={nc.nci} onClick={() => { setNcDetalle(nc); setVista("detalle"); }}
                style={{ background: nc.severidad==="critica"?(oscuro?"#1a0505":"#fff5f5"):cardBg, borderRadius: "12px", padding: "1.2rem 1.5rem", border: `1px solid ${nc.severidad==="critica"?"#e63946":borde}`, marginBottom: "10px", cursor: "pointer" }}
                onMouseEnter={e => e.currentTarget.style.boxShadow="0 4px 16px rgba(123,91,242,0.15)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow="none"}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
                      <span style={{ fontWeight: "700", color: "#e63946", fontSize: "13px" }}>{nc.nci}</span>
                      {nc.rip && <span style={{ fontSize: "10px", color: subtexto, background: oscuro?"#091840":"#f0f0f0", padding: "1px 6px", borderRadius: "6px" }}>RIP {nc.rip}</span>}
                      {nc.esFSC && <span style={{ fontSize: "10px", color: "#22c55e", fontWeight: "600", background: "#22c55e22", padding: "1px 6px", borderRadius: "6px" }}>🌿 FSC</span>}
                      {nc.tipoNC && <span style={{ fontSize: "10px", color: "#3b82f6", fontWeight: "600", background: "#3b82f622", padding: "1px 6px", borderRadius: "6px" }}>{nc.tipoNC}</span>}
                      {nc.maquinaNombre && <span style={{ fontSize: "10px", color: subtexto }}>⚙️ {nc.maquinaNombre}</span>}
                    </div>
                    <p style={{ fontSize: "13px", color: texto, fontWeight: "600", margin: "0 0 2px" }}>{nc.trabajo || nc.cliente || nc.proceso || "Sin descripción"}</p>
                    <p style={{ fontSize: "12px", color: subtexto, margin: "0 0 2px" }}>{nc.descripcion?.slice(0,70)}{nc.descripcion?.length>70?"...":""}</p>
                    <p style={{ fontSize: "11px", color: subtexto, margin: 0 }}>
                      {nc.origen && `Origen: ${nc.origen}`}{nc.deteccion && ` · ${nc.deteccion}`}{nc.motivo && ` · ${nc.motivo}`}
                    </p>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px", flexShrink: 0, marginLeft: "12px" }}>
                    <span style={{ fontSize: "11px", fontWeight: "700", color: estadosNC[nc.estado]?.color }}>{estadosNC[nc.estado]?.label}</span>
                    {nc.severidad && <span style={{ fontSize: "10px", fontWeight: "700", color: severidades[nc.severidad]?.color }}>{severidades[nc.severidad]?.label}</span>}
                    <span style={{ fontSize: "10px", fontWeight: "700", color: escalamientoColor[nc.escalamiento] }}>{escalamientoLabel[nc.escalamiento]}</span>
                    {nc.totalScrap && <span style={{ fontSize: "10px", color: "#f59e0b", fontWeight: "600" }}>💰 ${nc.totalScrap}</span>}
                  </div>
                </div>
                <div style={{ marginTop: "6px", fontSize: "10px", color: oscuro?"#7B5BF2":"#9b8cc4" }}>
                  {nc.fecha?.slice(0,16)} · {nc.creadoPor}{nc.op && ` · OP: ${nc.op}`}{nc.idPlanControl && ` · PC: ${nc.idPlanControl}`}
                </div>
              </div>
            ))
        }
      </div>
    </div>
  );
}
